import { ACTION_STATUS as STATUS, QUIZ_STATUS } from '../constants';
import { API } from '../api';
import * as types from '../constants/actions';
import { logActionError } from '../helpers/logger';

export const fetchQuizzesStatus = payload => ({
  type: types.FETCH_QUIZZES,
  payload,
});

export const fetchQuizzes =
  (username, page = 1) =>
  async dispatch => {
    try {
      dispatch(fetchQuizzesStatus({ status: STATUS.REQUEST, currentPage: page }));
      const {
        data: { result, pages, count },
      } = await API.quiz.fetchQuizzes(username, page);

      const { data: verifiedSkills } = await API.quiz.fetchVerifiedSkills(username);

      dispatch(
        fetchQuizzesStatus({ status: STATUS.SUCCESS, quizzes: mapQuizzes(result), verifiedSkills, pages, count })
      );
    } catch (error) {
      dispatch(fetchQuizzesStatus({ status: STATUS.FAILURE, error }));
      logActionError(types.FETCH_QUIZZES, error);
    }
  };

export const fetchQuizzesPublicStatus = payload => ({
  type: types.FETCH_QUIZZES_PUBLIC,
  payload,
});

export const fetchQuizzesPublic =
  (page = 1) =>
  async dispatch => {
    try {
      dispatch(fetchQuizzesPublicStatus({ status: STATUS.REQUEST, currentPage: page }));
      const {
        data: { result, pages, count },
      } = await API.quiz.fetchPublishedQuizzes(page);

      dispatch(fetchQuizzesPublicStatus({ status: STATUS.SUCCESS, quizzes: mapPublicQuizzes(result), pages, count }));
    } catch (error) {
      dispatch(fetchQuizzesPublicStatus({ status: STATUS.FAILURE, error }));
      logActionError(types.FETCH_QUIZZES_PUBLIC, error);
    }
  };

export const mapPublicQuizzes = quizzes =>
  quizzes.map(quiz => ({
    uuid: quiz.uuid,
    title: quiz.name || '',
    description: quiz.desc || '',
    attemptsRemaining: 3,
  }));

export const updateQuestion = payload => ({
  type: 'UPDATE_QUESTION',
  payload,
});

export const updateCurrentQuiz = payload => ({
  type: types.UPDATE_CURRENT_QUIZ,
  payload,
});

/**
 * __________________________    _ _ _ _ _ _ _ _
 *   ️Frontend Developer     -                   -
 *                          -    Secondary      -
 *   [Go to Quiz]           -    Card           -
 *                          -                   -
 * _________________________|    _ _ _ _ _ _ _ _|
 *
 */
const shouldDisplaySecondaryCard = quizStatus => {
  return (
    quizStatus === null ||
    quizStatus === QUIZ_STATUS.QUIZ_ONGOING ||
    quizStatus === QUIZ_STATUS.INTERVIEW_ONGOING ||
    quizStatus === QUIZ_STATUS.INTERVIEW_DECISION_PENDING
  );
};

export const mapQuizzes = quizzes =>
  quizzes
    .map(quiz => ({
      uuid: quiz.uuid,
      title: quiz.name || '',
      description: quiz.desc || '',
      skill: quiz.skill.name,
      attemptsRemaining: quiz.quiz_status.quiz_attempt_left,
      quizStatus: quiz.quiz_status.quiz_status,
      bookInterviewAfter: quiz.quiz_status.book_interview_after,
      quizResetAt: quiz.quiz_status.quiz_attempt_reset_at,
      interviewDateTime:
        quiz.quiz_status.interview &&
        quiz.quiz_status.interview.interview_slot &&
        quiz.quiz_status.interview.interview_slot.start,
      interviewUrl: quiz.quiz_status.interview && quiz.quiz_status.interview.interview_url,
      skillExpiresAt: quiz.quiz_status.skill_expire_at,
      skillVerifiedAt: quiz.quiz_status.skill_verified_at,
      skillVerified: quiz.quiz_status.skill_verified,
      resources: quiz.quiz_status.resources || [],
    }))
    .sort(
      (quizA, quizB) => shouldDisplaySecondaryCard(quizA.quizStatus) - shouldDisplaySecondaryCard(quizB.quizStatus)
    );

const updateStoreWithResult = data => (dispatch, getState) => {
  if (data.hasOwnProperty('status')) {
    dispatch(updateCurrentQuiz({ ...getState().quiz.currentQuiz, result: data.status }));
  }
};

export const submitResponseStatus = payload => ({
  type: types.SUBMIT_QUESTION_RESPONSE,
  payload,
});

export const submitResponse = answer => async dispatch => {
  try {
    dispatch(submitResponseStatus({ status: STATUS.REQUEST }));

    const { data } = await API.quiz.answer(answer);
    await dispatch(updateStoreWithResult(data));

    dispatch(submitResponseStatus({ status: STATUS.SUCCESS }));
  } catch (error) {
    dispatch(submitResponseStatus({ status: STATUS.FAILURE }));
    logActionError(types.SUBMIT_QUESTION_RESPONSE, error);
  }
};

export const submitSkipStatus = payload => ({
  type: types.SUBMIT_QUESTION_SKIP,
  payload,
});

export const submitSkip = () => async dispatch => {
  try {
    dispatch(submitSkipStatus({ status: STATUS.REQUEST }));

    const { data } = await API.quiz.skip();
    dispatch(updateStoreWithResult(data));

    dispatch(submitSkipStatus({ status: STATUS.SUCCESS }));
  } catch (error) {
    dispatch(submitSkipStatus({ status: STATUS.FAILURE }));
    logActionError(types.SUBMIT_QUESTION_SKIP, error);
  }
};

export const submitTimeoutStatus = payload => ({
  type: types.SUBMIT_QUESTION_TIMEOUT,
  payload,
});

export const submitTimeout = () => async dispatch => {
  try {
    dispatch(submitTimeoutStatus({ status: STATUS.REQUEST }));

    const { data } = await API.quiz.timeout();
    dispatch(updateStoreWithResult(data));

    dispatch(submitTimeoutStatus({ status: STATUS.SUCCESS }));
  } catch (error) {
    dispatch(submitTimeoutStatus({ status: STATUS.FAILURE }));
    logActionError(types.SUBMIT_QUESTION_TIMEOUT, error);
  }
};

export const submitSampleResponseStatus = payload => ({
  type: types.SUBMIT_SAMPLE_QUESTION_RESPONSE,
  payload,
});

export const submitSampleResponse = answer => async dispatch => {
  try {
    dispatch(submitSampleResponseStatus({ status: STATUS.REQUEST, answer }));

    // Induce a bit of randomness in the success response.
    // Thus the success action is dispatched in 200ms - 400ms
    setTimeout(
      () => {
        dispatch(submitSampleResponseStatus({ status: STATUS.SUCCESS }));
      },
      Math.random() * (400 - 200) + 200
    );
  } catch (error) {
    dispatch(submitSampleResponseStatus({ status: STATUS.FAILURE }));
  }
};
